import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Typeahead } from "react-bootstrap-typeahead";

import geo from "./geo";

function Address(props) {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [provider, setProvider] = useState(props.provider || "google");

  const debounce = (func, delay) => {
    let debounceTimer;
    return function (...args) {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  // Bug real (auditoría 2026-08-14): sin guard de secuencia, dos búsquedas
  // en vuelo a la vez (tipeo rápido) podían resolver DESORDENADAS — la
  // respuesta de una query VIEJA llegando después de la nueva pisaba las
  // opciones correctas con resultados de una búsqueda anterior. Tampoco
  // había cleanup al desmontar (setState sobre un componente ya
  // desmontado si el fetch seguía en vuelo).
  const requestSeqRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchOptions = async (query) => {
    const seq = ++requestSeqRef.current;
    setOptions([]);
    setSearching(true);

    const options =
      (await geo
        .autocomplete(provider, query, props.autocomplete)
        .catch(console.log)) || [];

    if (!mountedRef.current || seq !== requestSeqRef.current) return;

    setOptions(options);
    setSearching(false);
  };

  const onInputChange = useCallback(
    debounce((query) => {
      if (query.length >= 2) {
        fetchOptions(query);
      }
    }, 500),
    [provider, props.autocomplete]
  );

  useEffect(() => {
    // Bug real (auditoría 2026-08-14): (a) esta URL estaba hardcodeada al
    // dominio de PRODUCCIÓN — en QA/dev/preview este fetch pegaba igual
    // contra prod para saber qué provider de mapas usar, en vez del origin
    // real donde corre la app. (b) este efecto corría SIEMPRE e ignoraba
    // un provider explícito pasado por prop — el caller podía pedir
    // "opencage" y este fetch lo pisaba de vuelta con lo que devolviera el
    // endpoint apenas resolvía.
    if (props.provider) return;
    let cancelled = false;
    const fetchProvider = async () => {
      return fetch(`${window.location.origin}/api/maps-provider`).then(res => res.json()).then(rows => {
        if (cancelled) return;
        let row = rows.find(row => row.provider==="drapp");
        setProvider(row?.value)
      }).catch(() => null)
    };
    fetchProvider();
    return () => { cancelled = true };
  }, [props.provider]);

  const onChange = (value) => {
    if (props.onChange) props.onChange({ ...value });
  };

  const onSelect = async (array) => {
    const value = array[0] || {};
    onChange({ ...value });
    value.place_id = value.place_id || value.id || value.reference;
    if (value && value.place_id) {
      let details;
      if (provider === "opencage") {
        details = await geo.deatilsOpenCage(
          options.find((o) => o.place_id === value.place_id),
          provider
        );
      } else {
        details = await geo.details(provider, value.place_id).catch(console.log);
      }
      const payload = { ...value, ...details };
      if (details) onChange(payload);
    }
  };

  return (
    <Typeahead
      isLoading={!!searching}
      inputProps={{
        required: true,
        id: props.id || "address",
        autoComplete: "off",
        disabled: !!props.disabled,
      }}
      minLength={2}
      id={props.id || "address"}
      labelKey="description"
      onChange={onSelect}
      options={options}
      filterBy={() => true}
      onInputChange={onInputChange}
      selected={[props.value].filter((p) => p && p.description)}
      placeholder={
        props.placeholder || t(`address_placeholder`, { context: "address" })
      }
      newSelectionPrefix={null}
      emptyLabel={searching ? t(`searching`) : t(`no_matches`)}
      paginationText={t(`display_more_results`, { context: "address" })}
      promptText={t(`type_to_search`, { context: "address" })}
      searchText={t(`searching`, { context: "address" })}
      renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
        <div className="form-floating">
          <input
            {...inputProps}
            autoFocus={props.autoFocus}
            type="text"
            className={`form-control${props.borderless ? " border-0 rounded-0" : ""
              }${props.transparent ? " bg-transparent" : ""}`}
            // Bug real (auditoría 2026-08-16): sin el fallback (que sí se
            // aplica arriba, líneas 108/113, para el wrapper de Typeahead),
            // los dos callers reales que omiten `id` (Location/Form.js y
            // Onboarding/Component/Location.js) terminaban renderizando
            // literalmente id="floatingInputundefined" — un "undefined"
            // visible en el DOM, y riesgo de id duplicado si este componente
            // se usa 2 veces en una misma pantalla sin id explícito.
            id={`floatingInput${props.id || "address"}`}
            placeholder={
              props.placeholder ||
              t(`address_placeholder`, { context: "address" })
            }
            ref={(input) => {
              inputRef(input);
              referenceElementRef(input);
            }}
          />
          <label htmlFor={`floatingInput${props.id || "address"}`}>{props.label}</label>
        </div>
      )}
    />
  );
}

export default React.memo(Address);
